import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Menu,
  X,
  LogOut,
  User,
  Users,
  BarChart3,
  Settings,
  ShoppingCart,
  Calendar,
  Wrench,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  DollarSign,
  Package,
  Calculator,
  PenTool,
  Clipboard,
  UserPlus,
  UserCog,
  Warehouse,
  ShieldCheck,
  FolderTree,
  Clock,
  PlayCircle,
  FileText,
  CheckCircle,
  TrendingDown,
  TrendingUp,
  Box,
  GraduationCap,
  BookOpen,
  Video,
  FileText as FileTextIcon,
  Link as LinkIcon,
  CreditCard,
  AlertTriangle,
  FolderOpen,
  Briefcase,
  Wallet,
  Layers,
  Percent,
  Palette,
  Star,
  Ruler,
  Tag,
  Maximize2,
  History,
  Coins,
  Hammer,
  Plus,
  Zap,
  Search,
  SearchX,
  HelpCircle
} from 'lucide-react';
import { useAllowedClientCreateRoles } from '../hooks/useClientAccess';
import NotificationsBell from './NotificationsBell';
import BirthdayIcon from './BirthdayIcon';
import { hasPermission, hasAnyPermission, getSectorPermissions } from '../config/permissions';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout, hasRole, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: allowedClientRoles, isLoading: loadingClientRoles, isFetching: fetchingClientRoles } = useAllowedClientCreateRoles();
  const [canSeeClientCreate, setCanSeeClientCreate] = useState(false);

  // Estado para o relógio de Brasília
  const [serverTime, setServerTime] = useState(new Date());

  // Atualizar o relógio a cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      // Usar hora local do navegador (já está no fuso correto do usuário)
      setServerTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Obter permissões do usuário a partir do papel
  const userPermissions = useMemo(() => {
    if (!user?.role) return [];
    return getSectorPermissions(user.role);
  }, [user?.role]);

  useEffect(() => {
    // allowedClientRoles já é o array retornado pelo hook (não precisa de .data)
    if (Array.isArray(allowedClientRoles)) {
      setCanSeeClientCreate(allowedClientRoles.includes(user?.role));
    } else if (allowedClientRoles && Array.isArray(allowedClientRoles.data)) {
      // Fallback caso o formato seja diferente
      setCanSeeClientCreate(allowedClientRoles.data.includes(user?.role));
    }
  }, [allowedClientRoles, user?.role]);

  // Auto-expandir menus baseado na rota atual
  useEffect(() => {
    const path = location.pathname;
    setExpandedMenus(prev => {
      const newExpanded = { ...prev };
      let changed = false;

      // Comercial & Atendimento
      if (
        path.startsWith('/agendamentos') ||
        path.startsWith('/atendimentos') ||
        path.startsWith('/primeiro-contato') ||
        path.startsWith('/config/fila-espera') ||
        path.startsWith('/levantamentos') ||
        path.startsWith('/pre-projetos') ||
        path.startsWith('/croquis') ||
        path.startsWith('/clientes')
      ) {
        if (!newExpanded['comercial']) {
          newExpanded['comercial'] = true;
          changed = true;
        }
      }

      // Projetos & Produção
      if (
        path.startsWith('/projetos') ||
        path.startsWith('/carteira-projetos') ||
        path.startsWith('/projeto-rapido') ||
        path.startsWith('/cutting-plans') ||
        path.startsWith('/producao') ||
        path.startsWith('/demands')
      ) {
        if (!newExpanded['projetos-producao']) {
          newExpanded['projetos-producao'] = true;
          changed = true;
        }
      }

      // Orçamentos & Vendas
      if (
        path.startsWith('/quick-budget') ||
        path.startsWith('/orcamentos') ||
        path.startsWith('/condicoes-pagamento') ||
        path.startsWith('/budget-options') ||
        path.startsWith('/budget-margin-rules') ||
        path.startsWith('/budget-history') ||
        path.startsWith('/capacidade')
      ) {
        if (!newExpanded['orcamentos-vendas']) {
          newExpanded['orcamentos-vendas'] = true;
          changed = true;
        }
      }

      // Financeiro & Fiscal
      if (path.startsWith('/financeiro') || path.startsWith('/documentos-fiscais')) {
        if (!newExpanded['financeiro']) {
          newExpanded['financeiro'] = true;
          changed = true;
        }
      }

      // Estoque & Materiais
      if (
        path.startsWith('/estoque') ||
        path.startsWith('/fornecedores') ||
        path.startsWith('/armazens') ||
        path.startsWith('/custos') ||
        path.startsWith('/produtos')
      ) {
        if (!newExpanded['gestao-estoque']) {
          newExpanded['gestao-estoque'] = true;
          changed = true;
        }
      }

      // Gestão de Pessoas
      if (
        path.startsWith('/users') ||
        path.startsWith('/ceo/novo-funcionario') ||
        path.startsWith('/config/jornadas') ||
        path.startsWith('/adiantamento') ||
        path.startsWith('/meus-holerites') ||
        path.startsWith('/payslips')
      ) {
        if (!newExpanded['gestao-pessoas']) {
          newExpanded['gestao-pessoas'] = true;
          changed = true;
        }
        // Auto-expandir grupos aninhados baseado na rota
        if (path.startsWith('/ceo/novo-funcionario') || path === '/users' || path.startsWith('/users/salarios') || path.startsWith('/config/jornadas')) {
          const cadastroKey = 'gestao-pessoas-cadastro-e-configuração';
          if (!newExpanded[cadastroKey]) {
            newExpanded[cadastroKey] = true;
            changed = true;
          }
        }
        if (path.startsWith('/users/sabados-trabalhados') || path.startsWith('/users/horas-extras') || path.startsWith('/users/feriados') || path.startsWith('/users/divergencias') || path.startsWith('/users/ferias') || path.startsWith('/users/contratos')) {
          const controlesKey = 'gestao-pessoas-controles-de-trabalho';
          if (!newExpanded[controlesKey]) {
            newExpanded[controlesKey] = true;
            changed = true;
          }
        }
        if (path.startsWith('/payslips') || path.startsWith('/adiantamento/aprovacao') || path.startsWith('/users/vales') || path.startsWith('/users/contribuicoes-fiscais') || path.startsWith('/users/despesas-diversas') || path.startsWith('/users/plr')) {
          const pagamentosKey = 'gestao-pessoas-pagamentos-e-adiantamentos';
          if (!newExpanded[pagamentosKey]) {
            newExpanded[pagamentosKey] = true;
            changed = true;
          }
        }
        if ((path.startsWith('/adiantamento') && !path.startsWith('/adiantamento/aprovacao')) || path.startsWith('/meus-holerites')) {
          const painelKey = 'gestao-pessoas-painel-funcionário';
          if (!newExpanded[painelKey]) {
            newExpanded[painelKey] = true;
            changed = true;
          }
        }
      }

      // Ajuda & Treinamentos
      if (path.startsWith('/treinamentos') || path.startsWith('/manuais')) {
        if (!newExpanded['suporte-treinamentos']) {
          newExpanded['suporte-treinamentos'] = true;
          changed = true;
        }
      }

      // Configurações
      if (path.startsWith('/config') || path.startsWith('/admin') || path.startsWith('/auditoria')) {
        if (!newExpanded['configuracoes']) {
          newExpanded['configuracoes'] = true;
          changed = true;
        }
        if (
          path.startsWith('/config/agenda-levantamento') ||
          path.startsWith('/config/precos') ||
          path.startsWith('/config/pre-producao') ||
          path.startsWith('/config/fila-espera/scores') ||
          path.startsWith('/config/acabamento-3d') ||
          path.startsWith('/config/apresentacao')
        ) {
          const tecnicasKey = 'configuracoes-parâmetros-técnicos';
          if (!newExpanded[tecnicasKey]) {
            newExpanded[tecnicasKey] = true;
            changed = true;
          }
        }
      }

      return changed ? newExpanded : prev;
    });
  }, [location.pathname]);

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Configuração do menu baseada no role com estrutura hierárquica
  const getMenuStructure = () => {
    const menuStructure = [];

    // 1. Dashboard - sempre visível
    menuStructure.push({
      type: 'single',
      name: 'Dashboard',
      href: '/dashboard',
      icon: BarChart3,
      roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS', 'PRODUCAO', 'MONTAGEM_CLIENTE']
    });

    // 2. Atendimento & Comercial
    const comercialItems = [
      {
        name: 'Agendamentos',
        href: '/agendamentos',
        icon: CalendarDays,
        roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS']
      },
      {
        name: 'Meus Atendimentos',
        href: '/atendimentos',
        icon: PlayCircle,
        roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS', 'PRODUCAO', 'MONTAGEM_CLIENTE']
      },
      {
        name: '1º Contato',
        href: '/primeiro-contato',
        icon: UserPlus,
        roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS']
      },
      {
        name: 'Fila de Espera',
        href: '/config/fila-espera',
        icon: Clock,
        roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS']
      },
      {
        name: 'Levantamentos',
        href: '/levantamentos',
        icon: Clipboard,
        roles: ['GERENCIA', 'RECEPCAO_PROJETOS']
      },
      {
        name: 'Pré-Projetos',
        href: '/pre-projetos',
        icon: FileText,
        roles: ['GERENCIA', 'RECEPCAO_PROJETOS']
      },
      {
        name: 'Croquis 2D',
        href: '/croquis',
        icon: PenTool,
        roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']
      }
    ];

    const clientRoles = allowedClientRoles && Array.isArray(allowedClientRoles)
      ? allowedClientRoles
      : ['GERENCIA'];
    if (!clientRoles.includes('GERENCIA')) {
      clientRoles.push('GERENCIA');
    }
    comercialItems.push({
      name: 'Gestão de Clientes',
      href: '/clientes',
      icon: UserCog,
      roles: clientRoles,
      requiredPermission: 'clients:view'
    });

    menuStructure.push({
      type: 'group',
      key: 'comercial',
      name: 'Atendimento & Comercial',
      icon: UserPlus,
      items: comercialItems,
      roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS']
    });

    // 3. Projetos & Produção
    const projetosProducaoItems = [
      {
        name: 'Projetos',
        href: '/projetos',
        icon: FolderOpen,
        roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']
      },
      {
        name: 'Projetos em Carteira',
        href: '/carteira-projetos',
        icon: Briefcase,
        roles: ['GERENCIA', 'ADMIN', 'CEO']
      },
      {
        name: 'Projeto Rápido',
        href: '/projeto-rapido',
        icon: Zap,
        roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']
      },
      {
        name: 'Plano de Corte',
        href: '/cutting-plans',
        icon: Layers,
        roles: ['GERENCIA']
      },
      {
        name: 'Produção',
        href: '/producao',
        icon: Hammer,
        roles: ['GERENCIA', 'PRODUCAO', 'MONTAGEM_CLIENTE']
      },
      {
        name: 'Demandas',
        href: '/demands',
        icon: Clipboard,
        roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS', 'PRODUCAO', 'MONTAGEM_CLIENTE']
      }
    ];

    menuStructure.push({
      type: 'group',
      key: 'projetos-producao',
      name: 'Projetos & Produção',
      icon: FolderOpen,
      items: projetosProducaoItems,
      roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'ADMIN', 'CEO', 'PRODUCAO', 'MONTAGEM_CLIENTE']
    });

    // 4. Orçamentos & Vendas
    const orcamentosItems = [
      {
        name: 'Orçamento Rápido',
        href: '/quick-budget',
        icon: Zap,
        roles: ['GERENCIA', 'ADMIN', 'VENDAS']
      },
      {
        name: 'Lista de Orçamentos',
        href: '/orcamentos',
        icon: DollarSign,
        roles: ['GERENCIA', 'ADMIN']
      },
      {
        name: 'Condições de Pagamento',
        href: '/condicoes-pagamento',
        icon: CreditCard,
        roles: ['GERENCIA'],
        requiredPermission: 'settings:payment_conditions'
      },
      {
        name: 'Pesos da Ficha Técnica',
        href: '/budget-options',
        icon: Settings,
        roles: ['GERENCIA', 'ADMIN']
      },
      {
        name: 'Margens de Lucro',
        href: '/budget-margin-rules',
        icon: Percent,
        roles: ['GERENCIA', 'ADMIN']
      },
      {
        name: 'Histórico de Orçamentos',
        href: '/budget-history',
        icon: History,
        roles: ['GERENCIA', 'ADMIN']
      },
      {
        name: 'Contador de Capacidade',
        href: '/capacidade',
        icon: BarChart3,
        roles: ['GERENCIA', 'ADMIN']
      }
    ];

    menuStructure.push({
      type: 'group',
      key: 'orcamentos-vendas',
      name: 'Orçamentos & Vendas',
      icon: Calculator,
      items: orcamentosItems,
      roles: ['GERENCIA', 'ADMIN', 'VENDAS']
    });

    // 5. Financeiro & Fiscal
    const financeiroItems = [];
    const hasFinancialView = hasAnyPermission(user?.role, ['financial:full', 'financial:view', 'financial:expenses:view']);
    const hasFinancialFull = hasAnyPermission(user?.role, ['financial:full', 'financial:expenses']);

    if (hasFinancialView || hasFinancialFull) {
      if (hasAnyPermission(user?.role, ['financial:full', 'financial:view'])) {
        financeiroItems.push({
          name: 'Dashboard Financeiro',
          href: '/financeiro',
          icon: BarChart3
        });
        financeiroItems.push({
          name: 'Entradas',
          href: '/financeiro/entradas',
          icon: DollarSign
        });
      }

      financeiroItems.push({
        name: 'Saídas',
        href: '/financeiro/saidas',
        icon: DollarSign
      });

      if (hasAnyPermission(user?.role, ['financial:full', 'financial:view'])) {
        financeiroItems.push({
          name: 'Saídas Previstas',
          href: '/financeiro/saidas-previstas',
          icon: TrendingDown
        });
        financeiroItems.push({
          name: 'Contas a Pagar',
          href: '/financeiro/contas-a-pagar',
          icon: CreditCard
        });
        financeiroItems.push({
          name: 'Entradas Previstas',
          href: '/financeiro/entradas-previstas',
          icon: TrendingUp
        });
        financeiroItems.push({
          name: 'Folha',
          href: '/financeiro/folha',
          icon: FileText
        });
      }

      if (hasRole(['GERENCIA'])) {
        financeiroItems.push({
          name: 'Caixa da Empresa',
          href: '/financeiro/caixa-empresa',
          icon: Wallet
        });
        financeiroItems.push({
          name: 'Recebimentos',
          href: '/financeiro/recebimentos',
          icon: Coins
        });
        financeiroItems.push({
          name: 'Doc. Fiscais',
          href: '/documentos-fiscais',
          icon: FolderOpen
        });
        financeiroItems.push({
          name: 'Análise Detalhada',
          href: '/financeiro/folha/analise',
          icon: BarChart3
        });
        financeiroItems.push({
          name: 'Config. Indicadores',
          href: '/financeiro/folha/indicadores',
          icon: Settings
        });
      }
    }

    if (financeiroItems.length > 0) {
      menuStructure.push({
        type: 'group',
        key: 'financeiro',
        name: 'Financeiro & Fiscal',
        icon: DollarSign,
        items: financeiroItems,
        roles: ['GERENCIA']
      });
    }

    // 6. Estoque & Materiais
    const gestaoEstoqueItems = [];
    if (hasRole(['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'PRODUCAO', 'MONTAGEM_CLIENTE'])) {
      gestaoEstoqueItems.push({
        name: 'Estoque Geral',
        href: '/estoque',
        icon: Package
      });
    }
    if (hasRole(['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA'])) {
      gestaoEstoqueItems.push({
        name: 'Categorias de Produtos',
        href: '/estoque/categorias',
        icon: FolderTree
      });
      gestaoEstoqueItems.push({
        name: 'Fornecedores',
        href: '/fornecedores',
        icon: Users
      });
      gestaoEstoqueItems.push({
        name: 'Armazéns',
        href: '/armazens',
        icon: Warehouse
      });
      gestaoEstoqueItems.push({
        name: 'Custos de Produtos',
        href: '/custos',
        icon: Calculator
      });
    }
    if (hasRole(['GERENCIA'])) {
      gestaoEstoqueItems.push({
        name: 'Catálogo de Materiais',
        href: '/custos/catalogo',
        icon: Layers
      });
      gestaoEstoqueItems.push({
        name: 'Opções de Produtos',
        href: '/produtos/opcoes',
        icon: Box
      });
      gestaoEstoqueItems.push({
        name: 'Unidades de Medida',
        href: '/produtos/unidades',
        icon: Ruler
      });
      gestaoEstoqueItems.push({
        name: 'Marcas',
        href: '/produtos/marcas',
        icon: Tag
      });
      gestaoEstoqueItems.push({
        name: 'Cores',
        href: '/produtos/cores',
        icon: Palette
      });
      gestaoEstoqueItems.push({
        name: 'Tamanhos',
        href: '/produtos/tamanhos',
        icon: Maximize2
      });
    }

    if (gestaoEstoqueItems.length > 0) {
      menuStructure.push({
        type: 'group',
        key: 'gestao-estoque',
        name: 'Estoque & Materiais',
        icon: Package,
        items: gestaoEstoqueItems,
        roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'PRODUCAO', 'MONTAGEM_CLIENTE']
      });
    }

    // 7. Gestão de Pessoal (apenas GERENCIA)
    if (hasRole(['GERENCIA'])) {
      const gestaoPessoasItems = [
        {
          type: 'nested-group',
          name: 'Cadastro e Configuração',
          icon: UserCog,
          items: [
            { name: 'Novo Funcionário', href: '/ceo/novo-funcionario', icon: UserPlus },
            { name: 'Funcionários', href: '/users', icon: Users },
            { name: 'Salários', href: '/users/salarios', icon: DollarSign },
            { name: 'Jornadas de Trabalho', href: '/config/jornadas', icon: Calendar }
          ]
        },
        {
          type: 'nested-group',
          name: 'Controles de Trabalho',
          icon: Clock,
          items: [
            { name: 'Sábados Trabalhados', href: '/users/sabados-trabalhados', icon: Calendar },
            { name: 'Horas Extras', href: '/users/horas-extras', icon: Clock },
            { name: 'Feriados', href: '/users/feriados', icon: Calendar },
            { name: 'Divergências/Faltas', href: '/users/divergencias', icon: AlertTriangle },
            { name: 'Férias', href: '/users/ferias', icon: Calendar },
            { name: 'Contratos de Trabalho', href: '/users/contratos', icon: FileText }
          ]
        },
        {
          type: 'nested-group',
          name: 'Pagamentos e Adiantamentos',
          icon: CreditCard,
          items: [
            { name: 'Holerites', href: '/payslips', icon: FileText },
            { name: 'Aprovar Adiantamentos', href: '/adiantamento/aprovacao', icon: CheckCircle },
            { name: 'Bonificação', href: '/users/vales', icon: DollarSign },
            { name: 'Contribuições Fiscais (INSS/FGTS)', href: '/users/contribuicoes-fiscais', icon: FileText },
            { name: 'Vales / Empréstimos', href: '/users/despesas-diversas', icon: CreditCard },
            { name: 'Participação de Lucros (Gerência)', href: '/users/plr', icon: TrendingUp }
          ]
        },
        {
          type: 'nested-group',
          name: 'Painel Funcionário',
          icon: User,
          items: [
            { name: 'Adiantamento Quinzenal', href: '/adiantamento', icon: DollarSign },
            { name: 'Meus Holerites', href: '/meus-holerites', icon: FileText }
          ]
        }
      ];

      menuStructure.push({
        type: 'group',
        key: 'gestao-pessoas',
        name: 'Gestão de Pessoal',
        icon: Users,
        items: gestaoPessoasItems,
        roles: ['GERENCIA']
      });
    }

    // 8. Painel Funcionário (para Não-GERENCIA)
    if (!hasRole(['GERENCIA'])) {
      menuStructure.push({
        type: 'single',
        name: 'Adiantamento Quinzenal',
        href: '/adiantamento',
        icon: DollarSign,
        roles: ['RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS', 'PRODUCAO', 'MONTAGEM_CLIENTE']
      });
      menuStructure.push({
        type: 'single',
        name: 'Meus Holerites',
        href: '/meus-holerites',
        icon: FileText,
        roles: ['RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS', 'PRODUCAO', 'MONTAGEM_CLIENTE']
      });
      menuStructure.push({
        type: 'single',
        name: 'Meus Registros',
        href: '/meus-registros',
        icon: Clock,
        roles: ['RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS', 'PRODUCAO', 'MONTAGEM_CLIENTE']
      });

      menuStructure.push({
        type: 'group',
        key: 'transacoes',
        name: 'Transações',
        icon: History,
        roles: ['RECEPCAO_COMPRAS_AGENDA'],
        items: [
          { name: 'Nova Transação', href: '/financeiro/nova-transacao', icon: Plus },
          { name: 'Minhas Transações', href: '/financeiro/minhas-transacoes', icon: Wallet }
        ]
      });
    }

    // 9. Ajuda & Treinamentos
    menuStructure.push({
      type: 'group',
      key: 'suporte-treinamentos',
      name: 'Ajuda & Treinamentos',
      icon: GraduationCap,
      roles: ['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS', 'PRODUCAO', 'MONTAGEM_CLIENTE'],
      items: [
        { name: 'Treinamentos', href: '/treinamentos', icon: GraduationCap },
        { name: 'Manuais do Sistema', href: '/manuais', icon: BookOpen }
      ]
    });

    // 10. Configurações
    if (hasRole(['GERENCIA'])) {
      const configItems = [
        {
          name: 'Acesso aos Clientes',
          href: '/config/clientes-acesso',
          icon: UserCog
        },
        {
          name: 'Gerenciar Permissões',
          href: '/config/permissoes',
          icon: ShieldCheck
        },
        {
          name: 'Administração',
          href: '/admin',
          icon: Settings
        },
        {
          name: 'Auditoria',
          href: '/auditoria',
          icon: History
        },
        {
          type: 'nested-group',
          name: 'Parâmetros Técnicos',
          icon: Settings,
          items: [
            { name: 'Agenda Levantamento', href: '/config/agenda-levantamento', icon: CalendarDays },
            { name: 'Config. Preços', href: '/config/precos', icon: Percent },
            { name: 'Fluxo Pré-Produção', href: '/config/pre-producao', icon: Clock },
            { name: 'Scores Fila Espera', href: '/config/fila-espera/scores', icon: Star },
            { name: 'Acabamento 3D', href: '/config/acabamento-3d', icon: Palette },
            { name: 'Apresentação', href: '/config/apresentacao', icon: Clock }
          ]
        }
      ];

      menuStructure.push({
        type: 'group',
        key: 'configuracoes',
        name: 'Configurações',
        icon: Settings,
        items: configItems,
        roles: ['GERENCIA']
      });
    }

    return menuStructure.filter(item => {
      // Verificar roles
      if (item.roles && item.roles.length > 0 && !hasRole(item.roles)) {
        return false;
      }
      // Verificar permissão específica (se item.requiredPermission existe)
      if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
        return false;
      }
      return true;
    });
  };

  const menuStructure = getMenuStructure();

  // Função utilitária para normalizar strings (sem acentos e minúsculas)
  const normalizeSearchText = (text) =>
    (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  // Flattened list de todas as telas acessíveis para busca instantânea
  const flattenedNavItems = useMemo(() => {
    const list = [];

    const processItem = (item, parentCategory = '') => {
      if (item.type === 'single') {
        list.push({
          name: item.name,
          href: item.href,
          icon: item.icon,
          category: parentCategory || 'Geral'
        });
      } else if (item.type === 'group' && Array.isArray(item.items)) {
        item.items.forEach(subItem => {
          if (subItem.type === 'nested-group' && Array.isArray(subItem.items)) {
            subItem.items.forEach(nestedItem => {
              if (nestedItem.roles && !hasRole(nestedItem.roles)) return;
              if (nestedItem.requiredPermission && !hasPermission(nestedItem.requiredPermission)) return;
              list.push({
                name: nestedItem.name,
                href: nestedItem.href,
                icon: nestedItem.icon,
                category: `${item.name} > ${subItem.name}`
              });
            });
          } else {
            if (subItem.roles && !hasRole(subItem.roles)) return;
            if (subItem.requiredPermission && !hasPermission(subItem.requiredPermission)) return;
            list.push({
              name: subItem.name,
              href: subItem.href,
              icon: subItem.icon,
              category: item.name
            });
          }
        });
      }
    };

    menuStructure.forEach(item => processItem(item));

    const seen = new Set();
    return list.filter(item => {
      const key = `${item.href}-${item.name}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [menuStructure, hasRole, hasPermission]);

  // Resultados da busca em tempo real
  const searchResults = useMemo(() => {
    const query = normalizeSearchText(searchQuery.trim());
    if (!query) return [];

    return flattenedNavItems.filter(item => {
      const matchName = normalizeSearchText(item.name).includes(query);
      const matchCategory = normalizeSearchText(item.category).includes(query);
      const matchHref = normalizeSearchText(item.href).includes(query);
      return matchName || matchCategory || matchHref;
    });
  }, [searchQuery, flattenedNavItems]);

  const handleSelectNav = (href) => {
    navigate(href);
    setSearchQuery('');
    setSidebarOpen(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      e.preventDefault();
      handleSelectNav(searchResults[0].href);
    } else if (e.key === 'Escape') {
      setSearchQuery('');
    }
  };

  // Função auxiliar para verificar se uma rota está ativa
  const isRouteActive = (href, currentPath) => {
    // Se for exatamente igual, está ativo
    if (currentPath === href) return true;

    // Se o href for /users, só está ativo se for exatamente /users (não /users/salarios, etc)
    if (href === '/users') {
      return currentPath === '/users';
    }

    // Para outras rotas, verifica se começa com o href
    return currentPath.startsWith(href + '/') || currentPath.startsWith(href + '?');
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      GERENCIA: 'bg-red-100 text-red-800',
      RECEPCAO_COMPRAS_AGENDA: 'bg-blue-100 text-blue-800',
      RECEPCAO_PROJETOS: 'bg-green-100 text-green-800',
      PRODUCAO: 'bg-yellow-100 text-yellow-800',
      MONTAGEM_CLIENTE: 'bg-orange-100 text-orange-800'
    };
    return colors[role] || 'bg-secondary-100 text-secondary-800';
  };

  const renderNavContent = (isMobile = false) => {
    const handleItemClick = (href) => {
      navigate(href);
      setSearchQuery('');
      if (isMobile) {
        setSidebarOpen(false);
      }
    };

    return (
      <div className="flex flex-col h-full">
        {/* Campo de Busca no Sidebar */}
        <div className="px-3 pb-3 pt-1 sticky top-0 z-10 bg-white border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Buscar tela ou função..."
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white text-gray-800 placeholder-gray-400 transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Limpar busca (Esc)"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Conteúdo da navegação */}
        <div className="flex-1 px-3 py-3 overflow-y-auto space-y-1">
          {searchQuery.trim().length > 0 ? (
            <div>
              <div className="px-2 py-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider flex justify-between items-center mb-1">
                <span>Resultados ({searchResults.length})</span>
                <span className="text-[10px] text-gray-400 font-normal">Enter para ir</span>
              </div>
              {searchResults.length === 0 ? (
                <div className="px-3 py-8 text-center text-gray-400">
                  <SearchX className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-xs font-medium text-gray-600">Nenhuma tela encontrada</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Tente outro termo de busca</p>
                </div>
              ) : (
                searchResults.map((item) => {
                  const isActive = isRouteActive(item.href, location.pathname);
                  const ItemIcon = item.icon || Layers;
                  return (
                    <a
                      key={`${item.href}-${item.name}`}
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleItemClick(item.href);
                      }}
                      className={`group flex items-center justify-between px-2.5 py-2 text-xs rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary-100 text-primary-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center min-w-0 pr-2">
                        <ItemIcon className="mr-2.5 h-4 w-4 flex-shrink-0 text-gray-500 group-hover:text-primary-600" />
                        <div className="truncate">
                          <span className="font-medium text-gray-800 block truncate">{item.name}</span>
                          <span className="text-[10px] text-gray-400 block truncate">{item.category}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-3 w-3 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
                    </a>
                  );
                })
              )}
            </div>
          ) : (
            menuStructure.map((item) => {
              if (item.type === 'single') {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleItemClick(item.href);
                    }}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-secondary-700 hover:bg-secondary-100'
                    }`}
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </a>
                );
              } else if (item.type === 'group') {
                const isExpanded = expandedMenus[item.key];
                const hasActiveChild = item.items.some(subItem => {
                  if (subItem.type === 'nested-group' && Array.isArray(subItem.items)) {
                    return subItem.items.some(nestedItem => isRouteActive(nestedItem.href, location.pathname));
                  }
                  return isRouteActive(subItem.href, location.pathname);
                });
                return (
                  <div key={item.key}>
                    <button
                      onClick={() => toggleMenu(item.key)}
                      className={`w-full group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        hasActiveChild
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-secondary-700 hover:bg-secondary-100'
                      }`}
                    >
                      <div className="flex items-center">
                        <item.icon className="mr-3 h-5 w-5" />
                        {item.name}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-secondary-200 pl-2">
                        {item.items.map((subItem) => {
                          if (subItem.type === 'nested-group') {
                            const nestedKey = `${item.key}-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`;
                            const isNestedExpanded = expandedMenus[nestedKey];
                            const hasNestedActiveChild = subItem.items.some(nestedItem =>
                              isRouteActive(nestedItem.href, location.pathname)
                            );
                            return (
                              <div key={nestedKey} className="mb-2">
                                <button
                                  onClick={() => toggleMenu(nestedKey)}
                                  className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                                    hasNestedActiveChild
                                      ? 'bg-primary-50 text-primary-700 font-medium'
                                      : 'text-secondary-600 hover:bg-secondary-50'
                                  }`}
                                >
                                  <div className="flex items-center">
                                    <subItem.icon className="mr-2 h-4 w-4" />
                                    <span className="font-medium">{subItem.name}</span>
                                  </div>
                                  {isNestedExpanded ? (
                                    <ChevronDown className="h-3 w-3" />
                                  ) : (
                                    <ChevronRight className="h-3 w-3" />
                                  )}
                                </button>
                                {isNestedExpanded && (
                                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-primary-200 pl-2">
                                    {subItem.items.map((nestedItem) => {
                                      const isNestedActive = isRouteActive(nestedItem.href, location.pathname);
                                      return (
                                        <a
                                          key={nestedItem.href}
                                          href={nestedItem.href}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleItemClick(nestedItem.href);
                                          }}
                                          className={`flex items-center px-3 py-2 text-xs rounded-md transition-colors ${
                                            isNestedActive
                                              ? 'bg-primary-100 text-primary-700 font-medium'
                                              : 'text-secondary-600 hover:bg-secondary-50'
                                          }`}
                                        >
                                          <nestedItem.icon className="mr-2 h-3 w-3" />
                                          {nestedItem.name}
                                        </a>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          if (subItem.roles && !hasRole(subItem.roles)) {
                            return null;
                          }
                          if (subItem.requiredPermission && !hasPermission(subItem.requiredPermission)) {
                            return null;
                          }
                          const isActive = isRouteActive(subItem.href, location.pathname);
                          return (
                            <a
                              key={subItem.href}
                              href={subItem.href}
                              onClick={(e) => {
                                e.preventDefault();
                                handleItemClick(subItem.href);
                              }}
                              className={`flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                                isActive
                                  ? 'bg-primary-100 text-primary-700 font-medium'
                                  : 'text-secondary-600 hover:bg-secondary-50'
                              }`}
                            >
                              <subItem.icon className="mr-3 h-4 w-4" />
                              {subItem.name}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      {/* Sidebar para mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-secondary-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 flex w-full max-w-xs flex-col bg-white shadow-xl">
            <div className="flex h-16 items-center justify-between px-6 border-b border-secondary-200">
              <div className="flex items-center">
                <img src="/logo.png" alt="Sistema Santiago" className="h-16 w-auto" />
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 overflow-hidden flex flex-col pt-3">
              {renderNavContent(true)}
            </nav>
          </div>
        </div>
      )}

      {/* Sidebar para desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white border-r border-secondary-200">
          <div className="flex h-20 items-center justify-center px-6 border-b border-secondary-200">
            <img src="/logo.png" alt="Sistema Santiago" className="h-20 w-auto" />
          </div>
          <nav className="flex-1 overflow-hidden flex flex-col pt-3">
            {renderNavContent(false)}
          </nav>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="lg:pl-64">
        {/* Top navigation */}
        <div className="sticky top-0 z-40 bg-white border-b border-secondary-200">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-secondary-500 hover:text-secondary-700 lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex items-center space-x-4">
              {/* Relógio do servidor (Brasília) */}
              <div className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-3 py-1 bg-blue-50 rounded-lg border border-blue-200">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                <div className="text-xs sm:text-sm">
                  <span className="text-blue-800 font-medium">
                    {serverTime.toLocaleDateString('pt-BR')}
                  </span>
                  <span className="text-blue-600 mx-0.5 sm:mx-1">|</span>
                  <span className="text-blue-800 font-mono">
                    {serverTime.toLocaleTimeString('pt-BR')}
                  </span>
                </div>
                <span className="text-xs text-blue-500">(UTC-3)</span>
              </div>

              {/* Aniversariantes */}
              <BirthdayIcon />

              {/* Notificações */}
              <NotificationsBell />

              {/* Informações do usuário */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md p-2"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {user?.full_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-secondary-900">{user?.full_name}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>
                        {user?.role}
                      </span>
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-secondary-500" />
                </button>

                {/* Dropdown menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="py-1">
                      <a
                        href="/profile"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/profile');
                          setUserMenuOpen(false);
                        }}
                        className="flex items-center px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100"
                      >
                        <User className="mr-3 h-4 w-4" />
                        Meu Perfil
                      </a>
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100"
                      >
                        <LogOut className="mr-3 h-4 w-4" />
                        Sair
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo da página */}
        <main className="py-3 sm:py-4 lg:py-5">
          <div className="mx-auto w-full max-w-[1780px] px-2 sm:px-4 lg:px-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 